import type { RookieMemberEntity } from "@/modules/boxDashboard/modules/RookieMembers/entities/rookieMemberEntity"
import type { HUImage } from "@/modules/types/imageTypes"

export type RookieMemberModel = {
  id: string
  memberId: string
  fullName: string
  avatar: HUImage | null
  purchaseName: string | null
  lastSession: string | null
  nextSession: string | null
  daysLabel: string
  numberOfDays: number
  totalSessions: number
}

export type RookieMembersBlockModel = {
  members: RookieMemberModel[]
  totalCount: number
  hasMembers: boolean
  isEmpty: boolean
  showShowMore: boolean
  showShowLess: boolean
  showPagination: boolean
  currentPage: number
  totalPages: number
  displayedCount: number
}

function createRookieMemberModel(entity: RookieMemberEntity): RookieMemberModel {
  return {
    id: entity.id,
    memberId: entity.memberId,
    fullName: entity.getFullName(),
    avatar: entity.avatar,
    purchaseName: entity.purchaseName,
    lastSession: entity.getLastSessionDateFormatted(),
    nextSession: entity.getNextSessionDateFormatted(),
    daysLabel: entity.getDaysLabel(),
    numberOfDays: entity.nbOfDaysSinceRegistration,
    totalSessions: entity.totalSessions,
  }
}

export type CreateRookieMembersBlockModelParams = {
  members: RookieMemberEntity[]
  totalCount: number
  currentPage: number
  pageSize: number
  isExpanded: boolean
}

const INITIAL_DISPLAY_COUNT = 6

export function createRookieMembersBlockModel(params: CreateRookieMembersBlockModelParams): RookieMembersBlockModel {
  const { members, totalCount, currentPage, pageSize, isExpanded } = params

  const memberModels = members.map(createRookieMemberModel)
  const hasMembers = totalCount > 0
  const isEmpty = totalCount === 0

  // Determine display logic based on expansion and pagination
  let displayedMembers: RookieMemberModel[]
  let displayedCount: number

  if (isExpanded) {
    // When expanded, show all results for the current page
    displayedMembers = memberModels
    displayedCount = displayedMembers.length
  } else {
    // When collapsed, show first 6 members
    displayedCount = Math.min(memberModels.length, INITIAL_DISPLAY_COUNT)
    displayedMembers = memberModels.slice(0, displayedCount)
  }

  // Show "Show More" if more than 6 members and not expanded
  const showShowMore = !isExpanded && totalCount > INITIAL_DISPLAY_COUNT

  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    members: displayedMembers,
    totalCount,
    hasMembers,
    isEmpty,
    showShowMore,
    showShowLess: isExpanded,
    showPagination: totalPages > 1,
    currentPage,
    totalPages,
    displayedCount,
  }
}
